/**
 * Remote App Config — the live-control document served to the mobile apps.
 * Contract (backend built in parallel, versusapp):
 *   GET /api/v1/admin/app-config → { version:int, config:AppConfig }
 *   PUT /api/v1/admin/app-config   body = AppConfig → saves, version++
 *
 * Everything here is defensive: the backend may ship fields late or add new
 * ones, so we normalize whatever we get against DEFAULT_APP_CONFIG and keep
 * unknown top-level keys intact on save.
 */

export type PredictionOrderingMode = 'backend' | 'curated';

export interface PaymentsConfig {
  /** Payment methods pinned to the top of the deposit sheet, in order. */
  topOrder: string[];
}

export interface PredictionOrderingConfig {
  /** backend = ranking as delivered by the API · curated = admin-managed order. */
  mode: PredictionOrderingMode;
  /** Deterministic shuffle seed (int). */
  seed: number;
}

/** Overlay layout mode — a centered dark-glass card, or a full-screen takeover. */
export type PromoLayout = 'card' | 'fullscreen';

export interface PromoConfig {
  enabled: boolean;
  id: string;
  imageUrl: string;
  title: string;
  body: string;
  ctaText: string;
  /** Deeplink, e.g. versus://market/123 (built by the admin screen dropdown). */
  ctaRoute: string;
  /** ISO-8601, e.g. 2026-07-08T00:00:00Z */
  startsAt: string;
  endsAt: string;
  maxImpressions: number;
  // ── v2 (card geometry + voucher) ──────────────────────────────────────────
  /** 'card' = centered glass card · 'fullscreen' = edge-to-edge takeover. */
  layout: PromoLayout;
  /** Card corner radius 0..64 (card only). Fixes the fixed-32 circle bug. */
  cornerRadius: number;
  /** Card width 260..430 (card only); matches the legacy hardcoded 340. */
  cardWidth: number;
  /** Image area height 0..420 (card only); 0 = no image. Legacy = 210. */
  imageHeight: number;
  /** Voucher code shown with a copy button when non-empty (maxLength 64). */
  voucherCode: string;
}

export interface AppConfig {
  payments: PaymentsConfig;
  predictionOrdering: PredictionOrderingConfig;
  promo: PromoConfig;
  featureFlags: Record<string, boolean>;
  /** Unknown top-level keys from newer backends survive a round-trip. */
  [key: string]: unknown;
}

export const DEFAULT_APP_CONFIG: AppConfig = {
  payments: { topOrder: ['apple_pay', 'card'] },
  predictionOrdering: { mode: 'backend', seed: 0 },
  promo: {
    enabled: false,
    id: '',
    imageUrl: '',
    title: '',
    body: '',
    ctaText: '',
    ctaRoute: '',
    startsAt: '',
    endsAt: '',
    maxImpressions: 1,
    layout: 'card',
    cornerRadius: 32,
    cardWidth: 340,
    imageHeight: 210,
    voucherCode: '',
  },
  featureFlags: {},
};

/** Payment-method suggestions for the topOrder editor (free text also allowed). */
export const KNOWN_PAYMENT_METHODS = ['apple_pay', 'card', 'open_banking', 'google_pay'];

/**
 * One row of the bulk markets-ordering payload:
 *   PUT /api/v1/admin/markets/ordering  body = { items: MarketOrderingItem[] }
 * The admin markets list returns the same data snake_cased per market
 * (display_order / pinned / featured / dashboard_rank).
 */
export interface MarketOrderingItem {
  id: string;
  displayOrder: number;
  pinned: boolean;
  featured: boolean;
  /** Optional rank on the app dashboard; omit/null = not ranked. */
  dashboardRank?: number | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const asBool = (v: unknown, fallback: boolean): boolean =>
  typeof v === 'boolean' ? v : fallback;

const asString = (v: unknown, fallback: string): string =>
  typeof v === 'string' ? v : fallback;

const asInt = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : fallback;

/** Integer coercion clamped to [min,max]; non-numbers fall back (then clamp). */
const asIntClamped = (v: unknown, fallback: number, min: number, max: number): number => {
  const n = asInt(v, fallback);
  return Math.min(max, Math.max(min, n));
};

/** One of a fixed set of string literals, else the fallback literal. */
const asEnum = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;

/**
 * Merge an untrusted config payload (backend response or pasted JSON) into a
 * well-formed AppConfig. Never throws; unknown top-level keys are preserved.
 */
export function normalizeAppConfig(input: unknown): AppConfig {
  const raw = isRecord(input) ? input : {};
  const payments = isRecord(raw.payments) ? raw.payments : {};
  const ordering = isRecord(raw.predictionOrdering) ? raw.predictionOrdering : {};
  const promo = isRecord(raw.promo) ? raw.promo : {};
  const flagsRaw = isRecord(raw.featureFlags) ? raw.featureFlags : {};

  const featureFlags: Record<string, boolean> = {};
  Object.keys(flagsRaw).forEach((k) => {
    featureFlags[k] = Boolean(flagsRaw[k]);
  });

  const d = DEFAULT_APP_CONFIG;
  return {
    ...raw,
    payments: {
      topOrder: Array.isArray(payments.topOrder)
        ? payments.topOrder.filter((m): m is string => typeof m === 'string')
        : [...d.payments.topOrder],
    },
    predictionOrdering: {
      mode: ordering.mode === 'curated' ? 'curated' : 'backend',
      seed: asInt(ordering.seed, d.predictionOrdering.seed),
    },
    promo: {
      enabled: asBool(promo.enabled, d.promo.enabled),
      id: asString(promo.id, d.promo.id),
      imageUrl: asString(promo.imageUrl, d.promo.imageUrl),
      title: asString(promo.title, d.promo.title),
      body: asString(promo.body, d.promo.body),
      ctaText: asString(promo.ctaText, d.promo.ctaText),
      ctaRoute: asString(promo.ctaRoute, d.promo.ctaRoute),
      startsAt: asString(promo.startsAt, d.promo.startsAt),
      endsAt: asString(promo.endsAt, d.promo.endsAt),
      maxImpressions: asInt(promo.maxImpressions, d.promo.maxImpressions),
      layout: asEnum(promo.layout, ['card', 'fullscreen'] as const, d.promo.layout),
      cornerRadius: asIntClamped(promo.cornerRadius, d.promo.cornerRadius, 0, 64),
      cardWidth: asIntClamped(promo.cardWidth, d.promo.cardWidth, 260, 430),
      imageHeight: asIntClamped(promo.imageHeight, d.promo.imageHeight, 0, 420),
      voucherCode: asString(promo.voucherCode, d.promo.voucherCode).slice(0, 64),
    },
    featureFlags,
  };
}

/**
 * Structural validation for the raw-JSON editor. Returns human-readable
 * problems; an empty list means the document is safe to apply.
 */
export function validateAppConfigShape(input: unknown): string[] {
  const problems: string[] = [];
  if (!isRecord(input)) return ['Top level must be a JSON object.'];

  if ('payments' in input) {
    if (!isRecord(input.payments)) problems.push('"payments" must be an object.');
    else {
      const p = input.payments;
      if ('topOrder' in p) {
        if (!Array.isArray(p.topOrder) || p.topOrder.some((m) => typeof m !== 'string')) {
          problems.push('"payments.topOrder" must be an array of strings.');
        }
      }
    }
  }

  if ('predictionOrdering' in input) {
    if (!isRecord(input.predictionOrdering)) problems.push('"predictionOrdering" must be an object.');
    else {
      const o = input.predictionOrdering;
      if ('mode' in o && o.mode !== 'backend' && o.mode !== 'curated') {
        problems.push('"predictionOrdering.mode" must be "backend" or "curated".');
      }
      if ('seed' in o && (typeof o.seed !== 'number' || !Number.isInteger(o.seed))) {
        problems.push('"predictionOrdering.seed" must be an integer.');
      }
    }
  }

  if ('promo' in input) {
    if (!isRecord(input.promo)) problems.push('"promo" must be an object.');
    else {
      const promo = input.promo;
      if ('enabled' in promo && typeof promo.enabled !== 'boolean') {
        problems.push('"promo.enabled" must be a boolean.');
      }
      (['id', 'imageUrl', 'title', 'body', 'ctaText', 'ctaRoute', 'startsAt', 'endsAt', 'voucherCode'] as const).forEach((k) => {
        if (k in promo && typeof promo[k] !== 'string') problems.push(`"promo.${k}" must be a string.`);
      });
      if ('maxImpressions' in promo && (typeof promo.maxImpressions !== 'number' || !Number.isInteger(promo.maxImpressions))) {
        problems.push('"promo.maxImpressions" must be an integer.');
      }
      if ('layout' in promo && promo.layout !== 'card' && promo.layout !== 'fullscreen') {
        problems.push('"promo.layout" must be "card" or "fullscreen".');
      }
      (['cornerRadius', 'cardWidth', 'imageHeight'] as const).forEach((k) => {
        if (k in promo && (typeof promo[k] !== 'number' || !Number.isInteger(promo[k]))) {
          problems.push(`"promo.${k}" must be an integer.`);
        }
      });
    }
  }

  if ('featureFlags' in input) {
    if (!isRecord(input.featureFlags)) problems.push('"featureFlags" must be an object.');
    else {
      Object.entries(input.featureFlags).forEach(([k, v]) => {
        if (typeof v !== 'boolean') problems.push(`featureFlags["${k}"] must be a boolean.`);
      });
    }
  }

  return problems;
}
